import { useAlertNotifications } from '../../state/useAlertNotifications';
import { useAlertWords } from '../../state/alert-notifications/useAlertWords';
import { alertPlaceFocus } from '../../state/show-where';
import './Toast.css';

const SEVERITY_VARIANT: Record<string, string> = {
  critical: 'nx-toast--critical',
  warning: 'nx-toast--warning',
  info: 'nx-toast--info',
  error: 'nx-toast--critical',
};

export function ToastStack() {
  const {
    toasts,
    toastsWaiting,
    dismissToast,
    handleAlertAction,
    markRead,
    showAlertWhere,
    holdToast,
    releaseToast,
  } = useAlertNotifications();
  // Hotel words for a site audience, the engine's own text for ops
  // (plan W5 step 12). Not behind any flag: FOH get these toasts today.
  const wordsFor = useAlertWords();

  if (toasts.length === 0) return null;

  return (
    <div className="nx-toast-stack" role="region" aria-label="Alert toasts">
      {/* Alerts that arrived while a toast is held wait for it to be let
          go (`toast-stack.ts`). Said here, ABOVE the toasts: the stack is
          anchored at the bottom, so a line added on top moves nothing
          under the finger. */}
      {toastsWaiting > 0 && (
        <div className="nx-toast-stack__waiting" data-testid="toast-waiting">
          {toastsWaiting === 1 ? '1 more alert' : `${toastsWaiting} more alerts`}
        </div>
      )}
      {toasts.map((t) => {
        const words = wordsFor(t.alert);
        // Only for an alert about one place in the building: robot, lift,
        // door or task (`show-where.ts`). A gateway or site alert has no
        // place to show, and a button that lands on "this isn't about one
        // place" is a button that does nothing useful.
        const placeable = alertPlaceFocus(t.alert) !== null;
        return (
          // The holds (plan W4 step 13): while a pointer is over a toast,
          // focus is in it, or a finger is on it, the whole stack holds
          // still. Five seconds is too short to read hotel words AND reach
          // for "Show where" on a shared iPad.
          <div
            key={t.id}
            className={`nx-toast ${SEVERITY_VARIANT[t.alert.severity] ?? 'nx-toast--info'}`}
            data-testid={`toast-${t.alert.severity}`}
            data-held={(t.heldBy?.length ?? 0) > 0 ? 'true' : undefined}
            onMouseEnter={() => holdToast(t.id, 'hover')}
            onMouseLeave={() => releaseToast(t.id, 'hover')}
            // React's focus events bubble (focusin/focusout): focus moving
            // between the toast's own buttons releases and re-takes the
            // hold in one pass, before any sweep can expire it.
            onFocus={() => holdToast(t.id, 'focus')}
            onBlur={() => releaseToast(t.id, 'focus')}
            onTouchStart={() => holdToast(t.id, 'touch')}
            onTouchEnd={() => releaseToast(t.id, 'touch')}
            onTouchCancel={() => releaseToast(t.id, 'touch')}
          >
            <button
              type="button"
              className="nx-toast__main"
              onClick={() => {
                // Marking as read on click is the operator's explicit
                // "I saw this" gesture; handleAlertAction also routes to
                // the right screen -- for a site audience, the Overview,
                // pointed at the place. The markRead happens before
                // navigate so the toast's read state updates the badge
                // immediately on the way out.
                markRead(t.id);
                handleAlertAction(t.alert);
              }}
            >
              <div className="nx-toast__head">
                <span className="nx-toast__severity">{t.alert.severity.toUpperCase()}</span>
              </div>
              <div className="nx-toast__source">{words.source || 'Alert'}</div>
              {/* The subtitle is the engine's text too, so it goes with
                  the message it sat under. */}
              {!words.translated && t.alert.subtitle && (
                <div className="nx-toast__sub">{t.alert.subtitle}</div>
              )}
              <div className="nx-toast__msg">{words.message || '—'}</div>
            </button>
            {placeable && (
              <div className="nx-toast__actions">
                {/* Points; never opens a drawer. The Overview lands on the
                    place, and the person opens a panel if they want one. */}
                <button
                  type="button"
                  className="nx-toast__show-where"
                  onClick={() => showAlertWhere(t.alert)}
                  data-testid={`toast-show-where-${t.id}`}
                >
                  Show where
                </button>
              </div>
            )}
            <button
              type="button"
              className="nx-toast__dismiss"
              aria-label="Dismiss"
              onClick={() => dismissToast(t.id)}
              data-testid={`toast-dismiss-${t.id}`}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
